<script setup lang="ts">
    import { computed, toRef } from 'vue'
    import type { PropType } from 'vue'
    import { EditorState } from "@common/Common"
    import type { RawCriterionWeight } from "@Criteria/Model"
    import type { WeightsSetEditorViewModel, WeightsSetEditorCreateWeightsSetEventPayload, WeightsSetEditorUpdateWeightsSetEventPayload, WeightsSetEditorNormalizeWeightsEventPayload } from "@Criteria/Weights/Components/WeightsSetEditor"

    // ---------------- props / emits ----------------

    const props = defineProps({
        viewModel: {
            type: Object as PropType<WeightsSetEditorViewModel>,
            required: true
        }
    })

    const emit = defineEmits<{
        (e: 'create-weights-set', payload: WeightsSetEditorCreateWeightsSetEventPayload): void
        (e: 'update-weights-set', payload: WeightsSetEditorUpdateWeightsSetEventPayload): void
        (e: 'normalize-weights', payload: WeightsSetEditorNormalizeWeightsEventPayload): void
    }>()

    // ---------------- state ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- computed ----------------

    const opened = computed(() => vm.value.editorState !== EditorState.Closed)
    const initialized = computed(() => vm.value.initialized)
    const criteria = computed(() => vm.value.criteria)

    const nameError = computed(() =>
        vm.value.errors.find(e => e.ErrorCode?.startsWith("Name."))?.Error ?? null
    )

    const hasDirtyWeights = computed(() =>
        criteria.value.some(c => c.localWeight !== c.inputValue)
    )

    const title = computed(() =>
    {
        if (vm.value.editorState === EditorState.Creator) return "Create weights set"
        if (vm.value.editorState === EditorState.Editor) return "Edit weights set"
        return null
    })

    // ---------------- helpers ----------------

    function getError(criterionId: number): string | null
    {
        const err = vm.value.errors.find(e => e.Context === criterionId.toString())
        return err?.Error ?? null
    }

    // ---------------- actions ----------------

    function emitFormSubmit()
    {
        vm.value.errors = []

        const weights: RawCriterionWeight[] =
            criteria.value.map(c => ({ CriterionId: c.id, LocalWeight: c.inputValue }))

        if (vm.value.editorState === EditorState.Creator)
        {
            emit("create-weights-set", {
                name: vm.value.name,
                weights
            })
        }
        else if (vm.value.editorState === EditorState.Editor)
        {
            emit("update-weights-set", {
                weightsSetId: vm.value.weightsSetId!,
                name: vm.value.name,
                weights
            })
        }
    }

    function emitNormalizeWeights()
    {
        const weights: RawCriterionWeight[] =
            criteria.value.map(c => ({ CriterionId: c.id, LocalWeight: c.inputValue }))

        emit("normalize-weights", { weights })
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="opened">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-xl modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{title}}</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <h3 v-if="!initialized"><i class="fa fa-spinner fa-spin"></i> Loading... </h3>

                        <div v-if="initialized">


                            <form id="weights-set-editor-form" @submit.prevent="emitFormSubmit">
                                <div class="form-group">
                                    <label>Name:</label>
                                    <input v-model.trim="vm.name" type="text" class="form-control" required minlength="2" maxlength="100">
                                    <div class="error">{{nameError}}</div>
                                </div>

                                <br />
                                <table class="table table-bordered table-hover">
                                    <thead>
                                        <tr>
                                            <th rowspan="2">Criterion</th>
                                            <th rowspan="2" style="width: 150px;">Input</th>
                                            <th colspan="2">Normalized weight [%]</th>
                                        </tr>
                                        <tr>
                                            <th style="width: 150px;">Local</th>
                                            <th style="width: 150px;">Global</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-for="c in criteria" :key="c.id">
                                            <td>
                                                {{c.name}}<br /><small>{{c.path}}</small>
                                            </td>
                                            <td>
                                                <input v-if="c.isGoal" type="number" disabled value="100" class="form-control" />
                                                <input v-else type="number" v-model.number="c.inputValue" min="0" step="any" class="form-control" />
                                                <div class="error">{{getError(c.id)}}</div>
                                            </td>
                                            <td>
                                                <span v-if="c.isGoal">100</span>
                                                <span v-else>
                                                    <span v-if="!hasDirtyWeights">
                                                        {{ c.localWeight }}
                                                    </span>
                                                </span>
                                            </td>
                                            <td>
                                                <span v-if="c.isGoal">100</span>
                                                <span v-else>
                                                    <span v-if="!hasDirtyWeights">
                                                        {{ c.globalWeight }}
                                                    </span>
                                                </span>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </form>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button type="button" @click="close" class="btn btn-outline-secondary">Cancel</button>
                        <button type="button" v-if="hasDirtyWeights" @click="emitNormalizeWeights" class="btn btn-warning">Normalize</button>
                        <button type="submit" class="btn btn-primary" form="weights-set-editor-form">Save</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>